// SPDX-License-Identifier: AGPL-3.0-or-later
import { ExtensionUrl } from '@intakeform/fhir';
const DEMOGRAPHIC_MAP = {
    'first-name': (p) => stringValue(p.firstName ?? firstName(p.displayName)),
    'last-name': (p) => stringValue(p.lastName ?? lastName(p.displayName)),
    'full-name': (p) => stringValue(p.displayName),
    dob: (p) => dateValue(p.birthDate),
    'date-of-birth': (p) => dateValue(p.birthDate),
    gender: (p) => stringValue(p.gender),
    sex: (p) => stringValue(p.gender),
    email: (p) => stringValue(p.email),
    phone: (p) => stringValue(p.phone),
    'phone-number': (p) => stringValue(p.phone),
    'address-line-1': (p) => stringValue(p.addressLine1),
    'address-line-2': (p) => stringValue(p.addressLine2),
    'address-line1': (p) => stringValue(p.addressLine1),
    'address-line2': (p) => stringValue(p.addressLine2),
    city: (p) => stringValue(p.city),
    state: (p) => stringValue(p.state),
    province: (p) => stringValue(p.state),
    'postal-code': (p) => stringValue(p.postalCode),
    zip: (p) => stringValue(p.postalCode),
    country: (p) => stringValue(p.country),
};
// --------------------------------------------------------------- linkId match
/**
 * Resolving a questionnaire `linkId` to a demographic concept
 * (intakeform-preview-omk).
 *
 * The original table was an exact, case-sensitive lookup of 20 bare kebab-case
 * keys. Measured against the shipped template library, 35 of 43 demographic
 * items never matched — every jurisdiction-namespaced id (`ca-given-name`,
 * `mto-patient-first-name`, `eap-patient-dob`) missed, and so did any form
 * authored with different casing. Worse, a miss is not an error: it degrades to
 * "please fill in manually", which is indistinguishable from the EMR having no
 * data. Silent, and it looked like the EMR's fault.
 *
 * The replacement is deliberately conservative, in this order:
 *
 *   1. NORMALISE  — case, separators and camelCase collapse to tokens.
 *   2. REJECT     — if the id names a NON-PATIENT subject, stop. This runs
 *                   before scoring so no similarity score can ever override it.
 *   3. SCORE      — token-set and character-bigram similarity against concept
 *                   synonyms; accept only at or above MIN_MATCH_SCORE.
 *
 * Step 2 is the important one. `eap-prescriber-name` and
 * `mto-vision-examiner-name` are strong textual matches for a name field and
 * are NOT the patient. Writing patient data into a prescriber or witness field
 * on a medico-legal form is far worse than leaving it blank, so the asymmetry
 * is deliberate: a missed prefill costs typing, a wrong one corrupts a record.
 */
/**
 * Exact key first (cheap, and preserves the documented convention verbatim),
 * then the scored resolution. Exact wins so a deliberately-named item can
 * never be re-interpreted by similarity.
 */
function resolveDemographicMapper(linkId) {
    const exact = DEMOGRAPHIC_MAP[linkId];
    if (exact !== undefined)
        return exact;
    const resolved = resolveDemographicConcept(linkId);
    if (resolved === undefined)
        return undefined;
    return DEMOGRAPHIC_MAP[resolved.concept];
}
/** Accept a concept only at or above this similarity. */
export const MIN_MATCH_SCORE = 0.85;
/**
 * Tokens naming somebody who is NOT the patient. Presence of any of these
 * rejects the item outright, whatever it otherwise looks like. Kept broad on
 * purpose — the cost of a false reject is a manual entry.
 */
const NON_PATIENT_SUBJECT_TOKENS = new Set([
    'prescriber',
    'practitioner',
    'physician',
    'provider',
    'doctor',
    'clinician',
    'examiner',
    'assessor',
    'therapist',
    'pharmacist',
    'nurse',
    'surgeon',
    'witness',
    'guardian',
    'parent',
    'mother',
    'father',
    'spouse',
    'relative',
    'kin',
    'emergency',
    'guarantor',
    'attorney',
    'proxy',
    'surrogate',
    'agent',
    'representative',
    'employer',
    'insurer',
    'referring',
    'referrer',
    'subscriber',
    'beneficiary',
    'signature',
    'signatory',
    'interpreter',
    'caregiver',
    'contact',
]);
/**
 * Tokens that explicitly assert the item is about the PATIENT. Their presence
 * unlocks a more lenient pass (see resolveDemographicConcept) — but only after
 * the non-patient reject has already run, so `patient-proxy-name` is still
 * refused on `proxy`.
 */
const PATIENT_MARKERS = [
    'patient',
    'pt',
    'client',
    'subject',
    'self',
    'applicant',
    'resident',
    'student',
];
/** Form codes like omh154, ocf1, phq9 — namespacing, never meaning. */
const FORM_CODE_RE = /^[a-z]{2,6}\d{1,4}$/u;
/**
 * Namespacing noise: jurisdiction and form prefixes, plus words that merely
 * assert the subject IS the patient. Removed before scoring so
 * `mto-patient-first-name` reduces to `first name`.
 */
const NOISE_TOKENS = new Set([
    'ca',
    'us',
    'usa',
    'uk',
    'ny',
    'nys',
    'on',
    'ont',
    'qc',
    'bc',
    'ab',
    'mto',
    'eap',
    'wsib',
    'ohip',
    'phipa',
    'molst',
    'omh',
    'msp',
    'ocf',
    'mva',
    'roi',
    'hipaa',
    'vision',
    'medical',
    'health',
    'general',
    'info',
    'information',
    'details',
    // NB: 'form', 'section', 'field' and 'item' are deliberately NOT noise.
    // They are not jurisdiction namespaces, and stripping them lets a HEAD noun
    // vanish: 'form-name' would reduce to 'name' and resolve to the patient's
    // own name, which is a thing, not a person. Leaving them in place makes such
    // ids score below threshold and be refused — the safe direction.
    ...PATIENT_MARKERS,
]);
/** Concept → synonym phrases. Order is irrelevant; best score wins. */
const CONCEPT_SYNONYMS = {
    'first-name': [
        'first name',
        'given name',
        'forename',
        'first',
        'given',
        'fname',
        'firstname',
        'givenname',
    ],
    'last-name': [
        'last name',
        'family name',
        'surname',
        'last',
        'family',
        'lname',
        'lastname',
        'familyname',
    ],
    'full-name': ['name', 'full name', 'legal name', 'fullname', 'full legal name'],
    dob: ['dob', 'date of birth', 'birth date', 'birthdate', 'dateofbirth', 'born'],
    gender: ['gender', 'sex', 'gender identity'],
    email: ['email', 'email address', 'e mail', 'emailaddress'],
    phone: [
        'phone',
        'phone number',
        'telephone',
        'mobile',
        'cell',
        'tel',
        'phonenumber',
        'cell phone',
        'home phone',
    ],
    'address-line-1': [
        'address',
        'address line 1',
        'street address',
        'street',
        'address1',
        'addr1',
        'mailing address',
        'home address',
        'addressline1',
    ],
    'address-line-2': [
        'address line 2',
        'address2',
        'addr2',
        'apartment',
        'unit',
        'suite',
        'addressline2',
    ],
    city: ['city', 'town', 'municipality'],
    state: ['state', 'province', 'region'],
    'postal-code': ['postal code', 'zip', 'zip code', 'postcode', 'postalcode', 'zipcode'],
    country: ['country', 'nation'],
};
/** Every token appearing in any synonym — the concept vocabulary. */
const CONCEPT_VOCAB = new Set(Object.values(CONCEPT_SYNONYMS).flatMap((syns) => syns.flatMap((s) => s.split(' '))));
/** lowercase, split camelCase, unify separators, drop empties. */
export function normalizeLinkId(linkId) {
    return linkId
        .replace(/([a-z0-9])([A-Z])/gu, '$1 $2')
        .toLowerCase()
        .split(/[^a-z0-9]+/u)
        .filter((t) => t.length > 0);
}
/** Dice coefficient over two sets. */
function diceSets(a, b) {
    if (a.size === 0 || b.size === 0)
        return 0;
    let shared = 0;
    for (const x of a)
        if (b.has(x))
            shared += 1;
    return (2 * shared) / (a.size + b.size);
}
function bigrams(s) {
    const out = new Set();
    for (let i = 0; i < s.length - 1; i += 1)
        out.add(s.slice(i, i + 2));
    return out;
}
/**
 * Best-scoring demographic concept for a linkId, or undefined.
 *
 * Returns the score too so callers (and tests) can see how close a near-miss
 * was — a 0.83 is a signal to add a synonym, not to lower the threshold.
 */
export function resolveDemographicConcept(linkId) {
    const tokens = normalizeLinkId(linkId);
    if (tokens.length === 0)
        return undefined;
    // Hard reject BEFORE any scoring.
    for (const t of tokens) {
        if (NON_PATIENT_SUBJECT_TOKENS.has(t))
            return undefined;
    }
    let meaningful = tokens.filter((t) => !NOISE_TOKENS.has(t) && !FORM_CODE_RE.test(t));
    // When the id explicitly says "patient", remaining unknown tokens are form
    // qualifiers, not meaning: `mto-vision-patient-name` is the patient's name on
    // the vision form. Keep only vocabulary words so those qualifiers stop
    // diluting the score. Safe because the non-patient reject has already run —
    // this can only ever loosen matching for items that name the patient.
    if (tokens.some((t) => PATIENT_MARKERS.includes(t))) {
        const vocabOnly = meaningful.filter((t) => CONCEPT_VOCAB.has(t));
        if (vocabOnly.length > 0)
            meaningful = vocabOnly;
    }
    if (meaningful.length === 0)
        return undefined;
    const tokenSet = new Set(meaningful);
    const joined = meaningful.join('');
    let best;
    for (const [concept, synonyms] of Object.entries(CONCEPT_SYNONYMS)) {
        for (const syn of synonyms) {
            const synTokens = syn.split(' ').filter((t) => t.length > 0);
            const score = Math.max(diceSets(tokenSet, new Set(synTokens)), diceSets(bigrams(joined), bigrams(synTokens.join(''))));
            if (best === undefined || score > best.score)
                best = { concept, score };
        }
    }
    if (best === undefined || best.score < MIN_MATCH_SCORE)
        return undefined;
    return best;
}
/**
 * Build a draft QuestionnaireResponse from EMR context. Phase 1
 * recognises only the demographics convention above; any required item
 * that the mapper couldn't fill is surfaced as an `info`-severity
 * Finding so the admin sees a checklist of "still needs your input."
 *
 * Group / display items are skipped. Items whose answer would be an
 * empty string are also skipped — an unmapped optional field is not a
 * finding (it's just blank, the admin can fill if they want).
 */
export function prefill(args) {
    const { questionnaire, context } = args;
    const now = args.now ?? new Date();
    const items = [];
    const unmapped = [];
    walk(questionnaire.item, (item) => {
        // Prefer an explicit prefill-source extension when present; fall
        // back to the demographics linkId convention so existing forms
        // (built before the extension existed) keep working.
        const source = readPrefillSource(item.extension);
        const value = source !== undefined
            ? resolveSource(source, context)
            : resolveDemographicMapper(item.linkId)?.(context.patient);
        if (value === undefined) {
            if (item.required === true) {
                const reason = source === undefined && resolveDemographicMapper(item.linkId) === undefined
                    ? 'has no automatic source'
                    : 'was not found in the EMR record';
                unmapped.push({
                    severity: 'info',
                    code: 'incomplete',
                    message: `Item "${item.text ?? item.linkId}" ${reason} — please fill in manually.`,
                    linkId: item.linkId,
                });
            }
            return;
        }
        items.push({ linkId: item.linkId, answer: [value] });
    });
    const ref = `Questionnaire/${questionnaire.id ?? 'draft'}|${questionnaire.version ?? '1'}`;
    const response = {
        resourceType: 'QuestionnaireResponse',
        questionnaire: ref,
        status: 'in-progress',
        authored: now.toISOString(),
        item: items,
    };
    return { response, unmapped };
}
// ────────────────────────────── helpers ──────────────────────────────
/**
 * Read and parse the prefill-source extension from a QuestionnaireItem.
 * Returns undefined if the extension is missing, the JSON is invalid,
 * or the `kind` discriminator isn't one we recognise. Malformed JSON is
 * silent here — the engine will fall through to the linkId convention
 * map and the admin gets an unmapped-finding if the item is required.
 */
function readPrefillSource(extensions) {
    const ext = extensions?.find((e) => e.url === ExtensionUrl.prefillSource);
    const raw = ext?.valueString;
    if (typeof raw !== 'string' || raw.trim() === '')
        return undefined;
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch {
        return undefined;
    }
    if (parsed === null || typeof parsed !== 'object')
        return undefined;
    const obj = parsed;
    switch (obj.kind) {
        case 'patient':
        case 'observation':
        case 'condition':
        case 'medication':
        case 'fhir-coverage':
            return parsed;
        default:
            return undefined;
    }
}
/**
 * Dispatch a PrefillSource against the context bundle. Returns the
 * FHIR `value[x]` payload or undefined when no match (caller handles
 * the required-but-unmapped finding).
 */
function resolveSource(source, context) {
    switch (source.kind) {
        case 'patient': {
            const mapper = DEMOGRAPHIC_MAP[source.field];
            return mapper?.(context.patient);
        }
        case 'observation':
            return fillObservation(context.observations ?? [], source);
        case 'condition':
            return fillCondition(context.conditions ?? [], source);
        case 'medication':
            return fillMedication(context.medications ?? [], source);
        case 'fhir-coverage':
            return fillCoverage(context.coverage ?? [], source.field);
        default: {
            const _exhaustive = source;
            return _exhaustive;
        }
    }
}
/**
 * Project a field off the first active Coverage. We deliberately pick
 * the first one rather than aggregating — clinicians who want to
 * distinguish primary vs secondary insurance configure separate items
 * with the same source field; whichever pulls the active row first
 * wins. The status check skips cancelled / draft / entered-in-error
 * records so a stale entry doesn't shadow a real one.
 */
function fillCoverage(coverages, field) {
    const active = coverages.find((c) => (c.status ?? 'active') === 'active');
    if (active === undefined)
        return undefined;
    switch (field) {
        case 'payor':
            return stringValue(active.payor);
        case 'subscriberId':
        case 'member-id':
        case 'subscriber-id':
            return stringValue(active.subscriberId);
        case 'dependent':
            return stringValue(active.dependent);
        case 'group':
        case 'group-number':
            return stringValue(active.class?.[0]?.value);
        case 'relationship':
            return stringValue(active.relationship);
        default:
            return undefined;
    }
}
function codingMatches(coding, match) {
    if (coding.code !== match.code)
        return false;
    if (match.system !== undefined && coding.system !== match.system)
        return false;
    return true;
}
/** Latest matching observation, projected to a FHIR value[x] payload. */
function fillObservation(observations, source) {
    const matches = observations.filter((o) => o.code.coding.some((c) => codingMatches(c, source)));
    if (matches.length === 0)
        return undefined;
    // Sort by effectiveDateTime desc; missing dates sort last. Stable
    // sort means same-date observations preserve adapter order.
    const sorted = [...matches].sort((a, b) => (b.effectiveDateTime ?? '').localeCompare(a.effectiveDateTime ?? ''));
    const latest = sorted[0];
    if (latest === undefined)
        return undefined;
    if (latest.valueString !== undefined)
        return { valueString: latest.valueString };
    if (latest.valueBoolean !== undefined)
        return { valueBoolean: latest.valueBoolean };
    if (latest.valueInteger !== undefined)
        return { valueInteger: latest.valueInteger };
    if (latest.valueDecimal !== undefined)
        return { valueDecimal: latest.valueDecimal };
    if (latest.valueQuantity !== undefined) {
        return { valueDecimal: latest.valueQuantity.value };
    }
    if (latest.valueCodeableConcept !== undefined) {
        const first = latest.valueCodeableConcept.coding[0];
        if (first !== undefined)
            return { valueCoding: first };
    }
    return undefined;
}
/**
 * Returns valueBoolean(true) when an *active* condition with the given
 * code exists. Absence is deliberately not negated to false — a missing
 * condition could mean "patient doesn't have it" OR "not yet recorded",
 * and the engine can't tell them apart. Caller fills in manually.
 */
function fillCondition(conditions, source) {
    const match = conditions.find((c) => c.code.coding.some((coding) => codingMatches(coding, source)) &&
        isActiveCondition(c.clinicalStatus));
    if (match === undefined)
        return undefined;
    return { valueBoolean: true };
}
function isActiveCondition(status) {
    // FHIR R4 leaves clinicalStatus optional. Most servers populate it;
    // when absent, treat as active (the EMR returned it so it's relevant).
    if (status === undefined)
        return true;
    return status.coding.some((c) => c.code === 'active' || c.code === 'recurrence' || c.code === 'relapse');
}
/**
 * Medication routing has two shapes:
 *  - With a `code`: returns valueBoolean(true) iff an active medication
 *    statement matches (same yes/no semantics as Condition).
 *  - Without a `code`: returns valueString joining every active
 *    medication's display name, useful for "current medications" free-
 *    text fields.
 */
function fillMedication(medications, source) {
    const active = medications.filter((m) => (m.status ?? 'active') === 'active');
    if (source.code !== undefined) {
        const match = active.find((m) => m.medicationCodeableConcept?.coding.some((c) => codingMatches(c, { system: source.system, code: source.code })));
        if (match === undefined)
            return undefined;
        return { valueBoolean: true };
    }
    const names = active
        .map((m) => m.medicationCodeableConcept?.text ??
        m.medicationCodeableConcept?.coding[0]?.display ??
        m.medicationCodeableConcept?.coding[0]?.code)
        .filter((n) => typeof n === 'string');
    if (names.length === 0)
        return undefined;
    return { valueString: names.join(', ') };
}
function walk(items, visit) {
    if (items === undefined)
        return;
    for (const item of items) {
        if (item.type === 'group') {
            walk(item.item, visit);
            continue;
        }
        if (item.type === 'display')
            continue;
        visit(item);
    }
}
function stringValue(v) {
    if (v === undefined || v.trim() === '')
        return undefined;
    return { valueString: v };
}
function dateValue(v) {
    if (v === undefined || v.trim() === '')
        return undefined;
    return { valueDate: v };
}
function firstName(full) {
    const trimmed = full.trim();
    if (trimmed === '')
        return '';
    if (trimmed.includes(',')) {
        // "Family, Given" form — return the part after the comma.
        const after = trimmed.split(',')[1] ?? '';
        return after.trim().split(/\s+/u)[0] ?? '';
    }
    return trimmed.split(/\s+/u)[0] ?? '';
}
function lastName(full) {
    const trimmed = full.trim();
    if (trimmed === '')
        return '';
    if (trimmed.includes(',')) {
        return (trimmed.split(',')[0] ?? '').trim();
    }
    const parts = trimmed.split(/\s+/u);
    if (parts.length <= 1)
        return '';
    return parts[parts.length - 1] ?? '';
}
//# sourceMappingURL=prefill.js.map